import {
  browserSessionPersistence,
  EmailAuthProvider,
  getMultiFactorResolver,
  multiFactor,
  onIdTokenChanged,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  reauthenticateWithCredential,
  RecaptchaVerifier,
  sendPasswordResetEmail,
  setPersistence,
  signInWithEmailAndPassword,
  signOut,
  TotpMultiFactorGenerator,
  type MultiFactorError,
  type MultiFactorInfo,
  type MultiFactorResolver,
  type PhoneMultiFactorInfo,
  type TotpSecret,
  type User,
} from 'firebase/auth';
import { firebaseAuth } from './firebaseConfig';
import {
  firebaseErrorCode,
  mapFirebaseAuthError,
  MULTI_FACTOR_AUTH_REQUIRED_CODE,
} from './firebaseErrors';
import { AuthUnavailableError, TokenAcquisitionError } from './authErrors';
import { SIGNED_OUT_PATH } from './routes';
import type {
  AuthAccount,
  AuthProvider,
  PendingSmsCode,
  SecondFactorChallenge,
  SecondFactorSummary,
  SignInCredentials,
  SignInResult,
  SignOutOptions,
  TokenRequestOptions,
  TotpEnrollment,
} from './authProvider';

/** The issuer an authenticator app shows next to the account name. */
const TOTP_ISSUER = 'Tollit';

function toAuthAccount(user: User): AuthAccount {
  return { username: user.email ?? user.uid, localAccountId: user.uid };
}

// Cache the converted account by uid, so getAccount() returns the same
// AuthAccount object across calls when nothing changed — a subscriber reading
// this through useSyncExternalStore needs that stability, or it re-renders on
// every read.
let cachedUid: string | null = null;
let cachedAuthAccount: AuthAccount | null = null;

function toStableAuthAccount(user: User | null): AuthAccount | null {
  if (user?.uid !== cachedUid) {
    cachedUid = user?.uid ?? null;
    cachedAuthAccount = user ? toAuthAccount(user) : null;
  }
  return cachedAuthAccount;
}

/**
 * Firebase objects never cross the auth boundary. The callers hold opaque
 * handles; the resolver, verification id and TOTP secret behind each one live
 * here, and go away with the handle.
 */
const resolvers = new WeakMap<SecondFactorChallenge, MultiFactorResolver>();
const pendingSmsCodes = new WeakMap<
  PendingSmsCode,
  { verificationId: string; resolver: MultiFactorResolver | null }
>();
const totpSecrets = new WeakMap<TotpEnrollment, TotpSecret>();

function isPhoneInfo(info: MultiFactorInfo): info is PhoneMultiFactorInfo {
  return info.factorId === PhoneMultiFactorGenerator.FACTOR_ID;
}

function toSecondFactorSummaries(infos: readonly MultiFactorInfo[]): SecondFactorSummary[] {
  return infos.flatMap((info): SecondFactorSummary[] => {
    if (info.factorId === TotpMultiFactorGenerator.FACTOR_ID) {
      return [{ id: info.uid, kind: 'totp', label: info.displayName ?? '' }];
    }
    if (isPhoneInfo(info)) {
      return [
        { id: info.uid, kind: 'sms', label: info.displayName ?? '', maskedPhone: info.phoneNumber },
      ];
    }
    return [];
  });
}

// Same stability rule as the account cache above, keyed by which factors the
// user has rather than by uid alone: enrolling or removing one must produce a
// new array, and nothing else may.
let cachedFactorsKey: string | null = null;
let cachedFactors: readonly SecondFactorSummary[] = [];

function toStableSecondFactors(user: User | null): readonly SecondFactorSummary[] {
  const infos = user ? multiFactor(user).enrolledFactors : [];
  const key = `${user?.uid ?? ''}:${infos.map((info) => info.uid).join(',')}`;
  if (key !== cachedFactorsKey) {
    cachedFactorsKey = key;
    cachedFactors = toSecondFactorSummaries(infos);
  }
  return cachedFactors;
}

// Enrolling and removing factors refreshes the ID token, which fires
// onIdTokenChanged, but that is Firebase's implementation rather than its
// contract. These methods notify explicitly as well.
const localListeners = new Set<() => void>();
function notifyLocal(): void {
  localListeners.forEach((listener) => listener());
}

function currentUserOrThrow(): User {
  const user = firebaseAuth().currentUser;
  if (!user) throw new AuthUnavailableError(new Error('No signed-in user.'));
  return user;
}

/**
 * The password step's two outcomes. `auth/multi-factor-auth-required` is
 * caught BEFORE mapFirebaseAuthError: it means the password was right, and the
 * mapper would report it as AuthUnavailableError — "sign-in is temporarily
 * unavailable" for every enrolled user.
 *
 * Firebase's currentUser stays null until the challenge completes, so
 * AuthGuard and SignIn's redirect need no MFA awareness of their own.
 */
async function passwordStep(run: () => Promise<unknown>): Promise<SignInResult> {
  try {
    await run();
    return { status: 'signed-in' };
  } catch (error) {
    if (firebaseErrorCode(error) !== MULTI_FACTOR_AUTH_REQUIRED_CODE) {
      throw mapFirebaseAuthError(error);
    }
    const resolver = getMultiFactorResolver(firebaseAuth(), error as MultiFactorError);
    const challenge: SecondFactorChallenge = { factors: toSecondFactorSummaries(resolver.hints) };
    resolvers.set(challenge, resolver);
    return { status: 'second-factor-required', challenge };
  }
}

function resolverFor(challenge: SecondFactorChallenge): MultiFactorResolver {
  const resolver = resolvers.get(challenge);
  // Only reachable if a caller fabricated a challenge; a real one always has
  // its resolver for as long as the caller can still hold it.
  if (!resolver) throw new AuthUnavailableError(new Error('Unknown second-factor challenge.'));
  return resolver;
}

function pendingSmsCodeFor(pending: PendingSmsCode) {
  const entry = pendingSmsCodes.get(pending);
  if (!entry) throw new AuthUnavailableError(new Error('Unknown pending SMS code.'));
  return entry;
}

/**
 * SMS needs a reCAPTCHA; TOTP does not. A fresh invisible widget per send, in
 * a child element created for it and removed afterwards, so a resend or a
 * second attempt never trips over "reCAPTCHA has already been rendered in this
 * element", and nothing outlives the screen that asked.
 */
async function withRecaptcha<T>(
  container: HTMLElement,
  run: (verifier: RecaptchaVerifier) => Promise<T>,
): Promise<T> {
  const host = document.createElement('div');
  container.appendChild(host);
  const verifier = new RecaptchaVerifier(firebaseAuth(), host, { size: 'invisible' });
  try {
    return await run(verifier);
  } finally {
    verifier.clear();
    host.remove();
  }
}

export const firebaseAuthProvider: AuthProvider = {
  async initialize() {
    const auth = firebaseAuth();

    // A security decision, not a default: Firebase's own default
    // (browserLocalPersistence) survives browser restarts, which silently
    // reverses the shared-device call msalConfig.ts made on purpose
    // ('cacheLocation: sessionStorage'). Must run before anything else reads
    // auth state.
    await setPersistence(auth, browserSessionPersistence);

    // Firebase restores a persisted session asynchronously from IndexedDB, so
    // auth.currentUser is null for the first few hundred milliseconds of every
    // cold load even when the user is signed in. Rendering before this
    // resolves means the first component to ask a question gets the wrong
    // answer and bounces a signed-in user out — see main.tsx.
    await auth.authStateReady();
  },

  isAuthenticated() {
    return firebaseAuth().currentUser !== null;
  },

  getAccount() {
    return toStableAuthAccount(firebaseAuth().currentUser);
  },

  signIn({ email, password }: SignInCredentials) {
    return passwordStep(() => signInWithEmailAndPassword(firebaseAuth(), email, password));
  },

  async signOut(options: SignOutOptions = {}) {
    await signOut(firebaseAuth());
    // Firebase's signOut() resolves without navigating, unlike MSAL's
    // logoutRedirect. A hard navigation, not a router navigate(): it discards
    // all in-memory React state, which is the belt to the query cache's
    // braces (see useSignOut in sessionContext.tsx).
    window.location.assign(options.redirectTo ?? SIGNED_OUT_PATH);
  },

  async resetPassword(email: string) {
    try {
      await sendPasswordResetEmail(firebaseAuth(), email);
    } catch (error) {
      // Mapped like every other method here, even though the only caller
      // deliberately ignores the outcome: a raw Firebase error carries a
      // developer-facing message, and this is the boundary that stops those.
      throw mapFirebaseAuthError(error);
    }
  },

  async getToken(options: TokenRequestOptions = {}) {
    const user = firebaseAuth().currentUser;
    if (!user) throw new TokenAcquisitionError(new Error('No signed-in user.'));

    try {
      // getIdToken caches and refreshes internally. Do not cache the token
      // here, and do not read its `exp` to pre-empt expiry.
      return await user.getIdToken(options.forceRefresh);
    } catch (error) {
      throw new TokenAcquisitionError(error);
    }
  },

  subscribe(listener) {
    // onIdTokenChanged, not onAuthStateChanged: it fires on sign-in and
    // sign-out too, but also on every token refresh, which is the superset the
    // API client's view of "still signed in" wants.
    const unsubscribe = onIdTokenChanged(firebaseAuth(), () => listener());
    localListeners.add(listener);
    return () => {
      unsubscribe();
      localListeners.delete(listener);
    };
  },

  reauthenticate(password: string) {
    const user = currentUserOrThrow();
    // Dashboard accounts are email/password only; there is no other
    // credential to re-prove.
    const credential = EmailAuthProvider.credential(user.email ?? '', password);
    return passwordStep(() => reauthenticateWithCredential(user, credential));
  },

  async completeSignInWithTotp(challenge, factorId, code) {
    const resolver = resolverFor(challenge);
    try {
      await resolver.resolveSignIn(TotpMultiFactorGenerator.assertionForSignIn(factorId, code));
    } catch (error) {
      throw mapFirebaseAuthError(error);
    }
  },

  async sendSignInSms(challenge, factorId, recaptcha) {
    const resolver = resolverFor(challenge);
    const hint = resolver.hints.find((h) => h.uid === factorId);
    if (!hint) throw new AuthUnavailableError(new Error('Unknown second factor.'));

    try {
      const verificationId = await withRecaptcha(recaptcha, (verifier) =>
        new PhoneAuthProvider(firebaseAuth()).verifyPhoneNumber(
          { multiFactorHint: hint, session: resolver.session },
          verifier,
        ),
      );
      const pending: PendingSmsCode = { brand: 'PendingSmsCode' };
      pendingSmsCodes.set(pending, { verificationId, resolver });
      return pending;
    } catch (error) {
      throw mapFirebaseAuthError(error);
    }
  },

  async completeSignInWithSms(pending, code) {
    const { verificationId, resolver } = pendingSmsCodeFor(pending);
    if (!resolver) throw new AuthUnavailableError(new Error('Not a sign-in SMS code.'));
    try {
      const credential = PhoneAuthProvider.credential(verificationId, code);
      await resolver.resolveSignIn(PhoneMultiFactorGenerator.assertion(credential));
    } catch (error) {
      throw mapFirebaseAuthError(error);
    }
  },

  async startTotpEnrollment() {
    const user = currentUserOrThrow();
    try {
      const session = await multiFactor(user).getSession();
      const secret = await TotpMultiFactorGenerator.generateSecret(session);
      const enrollment: TotpEnrollment = {
        otpauthUri: secret.generateQrCodeUrl(user.email ?? user.uid, TOTP_ISSUER),
        secretKey: secret.secretKey,
      };
      totpSecrets.set(enrollment, secret);
      return enrollment;
    } catch (error) {
      throw mapFirebaseAuthError(error);
    }
  },

  async confirmTotpEnrollment(enrollment, code, label) {
    const user = currentUserOrThrow();
    const secret = totpSecrets.get(enrollment);
    if (!secret) throw new AuthUnavailableError(new Error('Unknown TOTP enrollment.'));
    try {
      await multiFactor(user).enroll(
        TotpMultiFactorGenerator.assertionForEnrollment(secret, code),
        label,
      );
    } catch (error) {
      throw mapFirebaseAuthError(error);
    }
    notifyLocal();
  },

  async sendEnrollmentSms(phoneNumber, recaptcha) {
    const user = currentUserOrThrow();
    try {
      const session = await multiFactor(user).getSession();
      const verificationId = await withRecaptcha(recaptcha, (verifier) =>
        new PhoneAuthProvider(firebaseAuth()).verifyPhoneNumber({ phoneNumber, session }, verifier),
      );
      const pending: PendingSmsCode = { brand: 'PendingSmsCode' };
      pendingSmsCodes.set(pending, { verificationId, resolver: null });
      return pending;
    } catch (error) {
      throw mapFirebaseAuthError(error);
    }
  },

  async confirmPhoneEnrollment(pending, code, label) {
    const user = currentUserOrThrow();
    const { verificationId } = pendingSmsCodeFor(pending);
    try {
      const credential = PhoneAuthProvider.credential(verificationId, code);
      await multiFactor(user).enroll(PhoneMultiFactorGenerator.assertion(credential), label);
    } catch (error) {
      throw mapFirebaseAuthError(error);
    }
    notifyLocal();
  },

  listSecondFactors() {
    return toStableSecondFactors(firebaseAuth().currentUser);
  },

  async removeSecondFactor(factorId) {
    const user = currentUserOrThrow();
    try {
      await multiFactor(user).unenroll(factorId);
    } catch (error) {
      // unenroll() withdraws the factor, then reloads the user. Withdrawing a
      // factor revokes the session, so that reload can fail with
      // user-token-expired after the removal itself succeeded. Every caller
      // signs the user out next and the following sign-in shows the real
      // factor list, so this is treated as done rather than as a failure.
      if (firebaseErrorCode(error) !== 'auth/user-token-expired') {
        throw mapFirebaseAuthError(error);
      }
    }
    notifyLocal();
  },
};
